import ImportantMessageBanner from "@/components/important-banner/ImportantMessageBanner";
import Label from "@/components/label/label";
import PaginatedSearchableSelectField from "@/components/selection-field/paginated-searchable-select-field";
import Tooltip from "@/components/Tooltip/Tooltip";
import { KEY_TYPES } from "@/constants/key-constants";
import { useLocale } from "@/contexts/localeContext";
import { TranslationKeys } from "@/contexts/TranslationKeys";
import { getArrayBufferFromBlob } from "@/exporters/LicenseHelper";
import { MultiSelectOption } from "@/models/MultiSelectOption";
import { getKskeyTypeWithPagination } from "@/services/ksKeyService";
import { validateIntegerWithinBounds, validateItemLength, validateItemName } from "@/utilities/Validation";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, useFormikContext } from "formik";
import { FC } from "react";
import { toast } from "react-toastify";
import UploadIcon from "../../../../images/upload.png";

interface GenerateImportKeyFormContentProps {
    action: "IMPORT" | "GENERATE";
}

const GenerateImportKeyFormContent: FC<GenerateImportKeyFormContentProps> = ({ action }) => {
    const { setFieldValue, values, errors } = useFormikContext<any>();
    const { translate } = useLocale();
    const KEYS_LENGTH: number = 32;
    const AID_MIN = 0;
    const AID_MAX = 16777215;

    const getKeyLabel = (ksKeyTypeId: string) => {
        switch (ksKeyTypeId) {
            case KEY_TYPES.PICC:
                return translate.byKey(TranslationKeys.piccKey);
            case KEY_TYPES.APERIO_MASTER:
                return translate.byKey(TranslationKeys.applicationId);
            case KEY_TYPES.DESFIRE_APP_MASTER:
                return translate.byKey(TranslationKeys.appMasterKey);
            case KEY_TYPES.DESFIRE_READ_WRITE:
                return translate.byKey(TranslationKeys.appReadWriteKey);
        }
    }

    const onKeyGenerate = () => {
        let result: number | string = '';

        if (values.ksKeyTypeId === KEY_TYPES.APERIO_MASTER) {
            const randomValue = Math.floor(Math.random() * (AID_MAX - AID_MIN + 1)) + AID_MIN;
            result = randomValue;
        }
        else {
            while (result.length < KEYS_LENGTH) {
                result += Math.random().toString(32).substring(2);
            }
            result = result.substring(0, KEYS_LENGTH);
        }

        setFieldValue('key', result);
    }

    const onBrowse = async (event) => {
        try {
            const file = event.target.files[0];
            if (!file) {
                return;
            }

            const arrayBuffer = await getArrayBufferFromBlob(file);
            const decoder = new TextDecoder("utf-8");
            const text = decoder.decode(arrayBuffer);
            setFieldValue('key', text);
            setFieldValue('fileName', file.name);
        } catch (error) {
            toast.error(translate.byKey(TranslationKeys.fileReadError));
        }
    };

    const restrictLength = (e, length) => {
        if (e.key !== 'Backspace' && e.key !== 'Tab' && (e.target.value.length === length || e.target.value.length > length)) {
            e.preventDefault();
        }
    }

    return <>
        <div className='flex flex-col py-1'>
            <div className="flex flex-row justify-between" id='typeLabel'>
                <Label htmlFor='ksKeyTypeId' className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.keyType)}:</Label>
            </div>
            <PaginatedSearchableSelectField
                name={translate.byKey(TranslationKeys.keyType)}
                key={"ksKeyTypeId"}
                accessor='ksKeyTypeId'
                dataMethod={getKskeyTypeWithPagination}
                searchAccessor={'trnName'}
                onSelect={(_, item: MultiSelectOption) => {
                    setFieldValue('ksKeyType', item?.label);
                    setFieldValue('ksKeyTypeId', item?.value);
                    if (values.key) {
                        setFieldValue('key', '');
                    }
                }}
                dataCy='typeField' />
        </div>
        <div className='flex flex-col'>
            <div className="flex flex-row justify-between py-1" id='nameLabel'>
                <Label htmlFor="name" className="text-xs text-gray-600" required>{translate.byKey(TranslationKeys.name)}</Label>
                {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
            </div>
            <div className="flex flex-col justify-between">
                <Field
                    type="text"
                    name="name"
                    data-cy="nameField"
                    id="nameInput"
                    className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                    validate={validateItemName} />
            </div>
        </div>
        <div className='flex flex-col w-full'>
            <div className="flex flex-row  justify-between py-1 w-full" id='keyLabel'>
                <div className="flex flex-row items-center">
                    <Label htmlFor="key" className="text-xs text-gray-600 mt-1" required>
                        {action === "GENERATE" ? getKeyLabel(values.ksKeyTypeId) : translate.byKey(TranslationKeys.fileName)}
                    </Label>
                    {values.ksKeyTypeId === KEY_TYPES.APERIO_MASTER && (
                        <span className="pt-1 pl-1">
                            <Tooltip text={translate.byKeyFormatted(TranslationKeys.applicationIDHint, { min: AID_MIN, max: AID_MAX })} direction="right" show={true} width={'w-full'}>
                                <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#3b82f6', opacity: "50%" }} />
                            </Tooltip>
                        </span>
                    )}
                </div>
                {errors.key ? <span className='text-red-600 text-xs'>{errors.key}</span> : null}
            </div>
            {action === "GENERATE" ?
                <div className="flex flex-col justify-between">
                    <Field
                        type={values.ksKeyTypeId === KEY_TYPES.APERIO_MASTER ? "number" : "text"}
                        name="key"
                        data-cy="keyField"
                        id="keyInput"
                        {...(values.ksKeyTypeId === KEY_TYPES.APERIO_MASTER ? { min: AID_MIN, max: AID_MAX } : {})}
                        {...(values.ksKeyTypeId === KEY_TYPES.APERIO_MASTER ? {} : { onKeyDown: (e) => restrictLength(e, KEYS_LENGTH) })}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                        validate={(value) => values.ksKeyTypeId === KEY_TYPES.APERIO_MASTER ? validateIntegerWithinBounds(value, AID_MIN, AID_MAX, translate, true) : validateItemLength(value, KEYS_LENGTH, translate)} />
                </div> :
                <div className="flex flex-row justify-between">
                    <Field
                        disabled
                        type="text"
                        name="fileName"
                        data-cy="fileNameField"
                        id="fileNameInput"
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-500 text-sm bg-gray-input w-full cursor-not-allowed" />
                    <div className='flex flex-row justify-between items-center ml-2 px-1 border rounded '>
                        <Label htmlFor='upload_keyFileInput' className='cursor-pointer mb-0'>
                            <img src={UploadIcon} alt="Browse key file" className="h-5 w-6" />
                        </Label>
                        <input
                            type='file'
                            accept='.key'
                            data-testid="upload_keyFileInput"
                            id='upload_keyFileInput'
                            className='hidden'
                            onChange={(e) => onBrowse(e)} />
                    </div>
                </div>
            }
        </div>
        <ImportantMessageBanner title={translate.byKey(TranslationKeys.importantWithColon)} message={translate.byKey(TranslationKeys.keyBackupWarning)} />
        <div className={`flex flex-row ${action === "GENERATE" ? "justify-between" : "justify-end"} mt-3 pl-1`}>
            {action === "GENERATE" ?
                <button
                    className={`py-1 px-4 rounded-full text-white bg-sidemenu-focus text-sm items-center`}
                    type="button"
                    onClick={onKeyGenerate}>
                    {translate.byKey(TranslationKeys.generate)}
                </button> : null}
            <button
                className={`py-1 px-4  secondary bg-lime rounded-full text-white text-sm items-center`}
                data-cy="submitButton"
                type="submit">
                {translate.byKey(TranslationKeys.submit)}
            </button>
        </div>
    </>
}

export default GenerateImportKeyFormContent;